import { Asset, ContentfulClientApi, Entry, EntryCollection, createClient } from 'contentful';
import isString from 'lodash/isString';
import { ContentfulClientConfig } from 'src/api/contentful/ContentfulClientConfig';
import { Environment } from 'src/utils/Environment';
import { LanguageCode } from 'src/utils/Locales';
import { flattenObject } from 'src/utils/Object';

export enum FilterOperators {
  EQUAL_TO = '',
  NOT_EQUAL_TO = '[ne]',
  EXISTS = '[exists]', // can be assigned to 'true'/'false'
  LESS_THAN = '[lt]',
  LESS_THAN_OR_EQUAL = '[lte]',
  GREATER_THAN = '[gt]',
  GREATER_THAN_OR_EQUAL = '[gte]',
}

export enum ArrayFilters {
  INCLUDES = '', // At least one of the items in the array matches the searched term.
  DOES_NOT_INCLUDE = '[ne]', // None of the items in the array matches the searched term.
  EXISTS = '[exists]', // can be assigned to 'true'/'false'
  MATCHES = '[all]', // Array matches a specific set of values.
  INCLUDES_ONE_OF = '[in]', // Array must contain at least one matching item.
  INCLUDES_ONE_NOT_OF = '[nin]', // Array must contain at least one NOT matching item.
}

export class Filter<ValidFilters extends string = FilterOperators | ArrayFilters> {
  readonly operator: ValidFilters;
  readonly value: Primitive;

  readonly ARRAY_INPUT_FEATURES: Set<string> = new Set([ArrayFilters.MATCHES, ArrayFilters.INCLUDES_ONE_OF, ArrayFilters.INCLUDES_ONE_NOT_OF ]);

  constructor(operator: ValidFilters, value: ValidFilters extends ArrayFilters ? Primitive | Primitive[] : Primitive) {
    this.operator = operator;

    if (Array.isArray(value)) {
      if (!this.ARRAY_INPUT_FEATURES.has(operator)) throw new Error(`Invalid filter. An array filter value is only valid for the ${ArrayFilters.MATCHES} operator.`);
      this.value = value
        .map(val => isString(val) ? val.replace(/,/, '\,') : val) // Escape any commas in the strings
        .join(','); // Join the array with commas
    } else {
      this.value = value;
    }
  }
}

/**
 * Converts an object type into a filter definition for the object.
 * Example:
 *   Filterify<{fields: { name: string; body: string; }}> == {fields?: { name?: Filter | Filter[]; body?: Filter | Filter[] }}
 */
type Filterify<T> = {
  // tslint:disable:ban-types
  [K in keyof T]?:
  T[ K ] extends string ? Primitive | Filter<FilterOperators> | Array<Filter<FilterOperators>> :
  T[ K ] extends number ? Primitive | Filter<FilterOperators> | Array<Filter<FilterOperators>> :
  T[ K ] extends boolean ? Primitive | Filter<FilterOperators> | Array<Filter<FilterOperators>> :
  T[ K ] extends undefined ? never :
  T[ K ] extends Function ? never :
  T[ K ] extends any[] ? Filter<ArrayFilters> | Array<Filter<ArrayFilters>> :
  T[ K ] extends object ? Filterify<T[ K ]> : // object - recurse
  any // Fallthrough - must be any type
  // tslint:enable:ban-types
};

export type EntryFilters<T extends object> = Filterify<Entry<T>>;

/**
 * Converts an object type into a two level keymap definition for the object.
 * Example:
 *   DeepKeyof<{fields: { name: string; body: string; }}> == { fields: Array<'name' | 'body'> | 'name' | 'body' }
 */
type DeepKeyof<T extends object> = {
  // tslint:disable-next-line:ban-types
  [K in keyof T]?: T[K] extends Function ? never : T[K] extends object ? Array<(keyof T[K])> | keyof T[K] : K
};

export interface ContentfulFilters<EntryType extends object = any> {
  contentType?: string;
  locale?: string; // Defaults to current i18n.language

  // The select operator allows you to choose what fields to return from an entity. Can only select properties to a depth of 2.
  select?: DeepKeyof<Entry<EntryType>> | EntryType;

  // Search for entries which have a field linking to a specific entry
  linksToEntry?: string;

  filters?: EntryFilters<EntryType>;

  // The maximum number of results returned
  limit?: number; // defaults to 100

  // By combining skip and limit you can paginate through results
  skip?: number;

  // order results by
  order?: string;

  // Default Props
  config?: ContentfulClientConfig;

  /**
   * When you have related content (e.g. entries with links to image assets) it's possible to include both
   *  search results and related data in a single request.
   * Using the include parameter, you can specify the number of levels to resolve.
   *
   * The maximum number of levels is 10.
   * The API will throw a BadRequestError for higher values or values other than an integer.
   * The default number, if the parameter is not specified, is 1. To omit linked items, specify include as 0.
   */
  include?: number;
}

/**
 * Recursively constructs the filter object
 */
function constructFilters(filters: EntryFilters<any>): Record<string, object | Primitive> {
  return Object.keys(filters)
    .reduce((result, key) => {
      const val: string | boolean | Filter | Filter[] | object = filters[key];
      if (val === null || val === undefined) {
        console.warn(`undefined or null value provided as a filter for ${key}. Did you mean to use EXISTS=false?`);
        return result;
      }

      if (isString(val) || val === true || val === false) {
        // Base cases: string or boolean - Assume equal to operator
        result[key] = val;
      } else if (val instanceof Filter) {
        // Base cases: Filter
        result[`${key}${val.operator}`] = val.value;
      } else if (Array.isArray(val)) {
        // Base cases: Filter[] - Loop over each value in the array
        val.forEach((filter: Filter) => {
          result[`${key}${filter.operator}`] = filter.value;
        });
      } else {
        // Recurse on object type
        result[key] = constructFilters(val);
      }
      return result;
    }, {} as Record<string, object | Primitive>) || {};
}

export function fetchEntries<EntryType extends object>(
  locale: string,
  { contentType, select, linksToEntry, filters, limit, order, skip, include }: ContentfulFilters<EntryType>,
  apiClient: ContentfulClientApi = createClient(Environment.getCloverdotcomContentfulConfig())
): Promise<EntryCollection<RecursivePartial<EntryType>>> {
  let query: any = {
    include: Math.min(Math.max(include || 1, 0), 10),
    locale,
    content_type: contentType || undefined,
    limit,
    skip,
    order,
    links_to_entry: linksToEntry || undefined,
  };

  if (select) {
    query.select = Object.keys(select)
      .map(key => {
        const props = select[key];

        if (Array.isArray(props)) return props.map(propName => `${key}.${propName}`);
        if (isString(props)) return [`${key}.${props}`];

        return [key]; // Else assume the user just wants all props nested under this key.
      }).reduce((a, b) => [...a, ...b], [])
      .join(',');
  }

  if (filters) {
    query = { ...query, ...flattenObject(constructFilters(filters)) };
  }

  return apiClient.getEntries<RecursivePartial<EntryType>>(query);
}

export async function fetchAsset(assetId: string, locale = LanguageCode.ENGLISH, api = Environment.current.shopContentApi): Promise<Asset> {
  return api.getAsset(assetId, { locale });
}
